/** V3.0.6 immersive onboarding wizard steps (single `/onboarding` URL). */
export type InsightWizardStep = "insight/1" | "insight/2" | "insight/3";

export type OnboardingWizardStep =
  | "score-intro"
  | "quiz/name"
  | "quiz/age"
  | "quiz/income"
  | "quiz/expenses"
  | "quiz/cash"
  | "quiz/debt"
  | "quiz/invest"
  | "quiz/risk"
  // V3.0.9 AI 取代焦虑广告承接（ad_source=AI_JOB_RISK 场景）新增，默认场景不走这四屏
  | "quiz/jobcat"
  | "quiz/jobrole"
  | "connect"
  | "result-ai-impact"
  | "risk-profile-loading"
  | InsightWizardStep
  | "goal"
  | "loading"
  | "loading-error"
  | "loading-sync-anomaly"
  | "fitness-score-result"
  | "result";

export const INSIGHT_STEPS: InsightWizardStep[] = [
  "insight/1",
  "insight/2",
  "insight/3",
];

export const isInsightWizardStep = (
  step: OnboardingWizardStep,
): step is InsightWizardStep =>
  step === "insight/1" || step === "insight/2" || step === "insight/3";

export const parseInsightIndex = (
  step: OnboardingWizardStep,
): 1 | 2 | 3 | null => {
  if (step === "insight/1") {
    return 1;
  }
  if (step === "insight/2") {
    return 2;
  }
  if (step === "insight/3") {
    return 3;
  }
  return null;
};

export type QuizProgressStep =
  | "quiz/name"
  | "quiz/age"
  | "quiz/income"
  | "quiz/expenses"
  | "quiz/cash"
  | "quiz/debt"
  | "quiz/invest"
  | "quiz/risk";

/** 组1 该上桌 / 组2 止血 / 组3 已在桌上 / 组4 存不下 / 组5 兜底 */
export type CohortGroup =
  | "ready_to_invest"
  | "stop_bleeding"
  | "already_investing"
  | "cant_save"
  | "fallback";

export type AgeBand = "A" | "B" | "C";

export type GoalId =
  | "money-leak"
  | "idle-cash"
  | "portfolio-risk"
  | "debt-free"
  | "million"
  | "home"
  | "retirement";

export type GoalResultVariant =
  | "money-leak"
  | "idle-cash"
  | "portfolio-risk"
  | "debt-free"
  | "million"
  | "home"
  | "retirement"
  | "runway";

export type SliderField = "income" | "expenses" | "cash" | "debt" | "invest";

export type SliderTouched = Record<SliderField, boolean>;

export type QuizAnswers = {
  name: string;
  /** 精确整数年龄（验收 P0-1：18–100，不再使用区间中位数） */
  age: number | null;
  income: number;
  expenses: number;
  cash: number;
  debt: number;
  invest: number;
  risk: string;
  /** V3.0.9：工作大类 key（`occupations.json` 的 categoryId）。默认场景不问，恒 "" */
  jobCategory: string;
  /** V3.0.9：具体职位 key（`occupations.json` 的 occupation key）。默认场景不问，恒 "" */
  jobRole: string;
  goalId: string;
  sliderTouched: SliderTouched;
};

export const createEmptySliderTouched = (): SliderTouched => ({
  income: false,
  expenses: false,
  cash: false,
  debt: false,
  invest: false,
});

export const createAllTouchedSliderTouched = (): SliderTouched => ({
  income: true,
  expenses: true,
  cash: true,
  debt: true,
  invest: true,
});

/**
 * @deprecated 步骤机一律走 `sequence/getQuizSequence`（场景化编排）。
 * 本常量**保留**，仅供两处场景无关的消费方使用：
 * `onboardingWizardSessionStorage`（校验持久化 step + 算 furthest 次序）与
 * `onboardingHarnessQuery`（枚举可跳转 step）。它同时是 AC1 回归底线的字面量基准 ——
 * `scripts/verify-onboarding-schema.mjs` 校验 7 拿它比对 `default` 场景解析结果。
 */
export const QUIZ_PROGRESS_STEPS: QuizProgressStep[] = [
  "quiz/name",
  "quiz/age",
  "quiz/income",
  "quiz/expenses",
  "quiz/cash",
  "quiz/debt",
  "quiz/invest",
  "quiz/risk",
];

/** 需要完整问卷答案才能渲染的步骤（画像组）；恢复时答案不完整须回第一道未完成题。 */
export const PROFILE_ANSWER_STEPS: ReadonlySet<OnboardingWizardStep> = new Set([
  "risk-profile-loading",
  "insight/1",
  "insight/2",
  "insight/3",
  "goal",
]);

/**
 * @deprecated 同 {@link QUIZ_PROGRESS_STEPS} —— 步骤机走 `sequence/getWizardStepOrder(scene)`。
 * 这里是 `default` 场景的屏序，也是 AC1 回归底线的字面量基准。
 * ⚠️ 不含 `ai_job_risk` 场景的 4 个新 step（`quiz/jobcat` / `quiz/jobrole` / `connect` /
 * `result-ai-impact`）—— 场景无关的消费方按需在 Slice 8「恢复策略」统一收口。
 */
/**
 * **全部已知 step**，与场景无关 —— 用于「这个字符串是不是一个合法 step」这类校验。
 *
 * 为什么不能拿 {@link WIZARD_STEP_ORDER} 当这件事用：那是**默认支的屏序**，
 * 不含 `ai_job_risk` 的 `connect` / `result-ai-impact`（也不含从不在序里的
 * `loading-error` / `loading-sync-anomaly`）。拿它校验会把广告支的合法 step
 * 判成非法 → 持久化的进度读不回来，且**不会报错**，只会静默回起点。
 */
export const ALL_WIZARD_STEPS: ReadonlySet<OnboardingWizardStep> = new Set([
  "score-intro",
  "quiz/name",
  "quiz/age",
  "quiz/income",
  "quiz/expenses",
  "quiz/cash",
  "quiz/debt",
  "quiz/invest",
  "quiz/risk",
  "quiz/jobcat",
  "quiz/jobrole",
  "connect",
  "result-ai-impact",
  "risk-profile-loading",
  ...INSIGHT_STEPS,
  "goal",
  "loading",
  "loading-error",
  "loading-sync-anomaly",
  "fitness-score-result",
  "result",
]);

export const WIZARD_STEP_ORDER: OnboardingWizardStep[] = [
  "score-intro",
  ...QUIZ_PROGRESS_STEPS,
  "risk-profile-loading",
  ...INSIGHT_STEPS,
  "goal",
  "loading",
  "fitness-score-result",
  "result",
];

export interface OnboardingWizardStepHandlers {
  step: OnboardingWizardStep;
  answers: QuizAnswers;
  quizProgressIndex: number;
  quizProgressTotal: number;
  resultVariant: GoalResultVariant;
  displayName: string;
  goNext: (answersOverride?: QuizAnswers) => void;
  goBack: () => void;
  setAnswer: <K extends keyof QuizAnswers>(
    key: K,
    value: QuizAnswers[K],
  ) => void;
  setSliderField: (key: SliderField, value: number, touched: boolean) => void;
  retryLoading: () => void;
  completeLoading: () => void;
  failLoading: () => void;
  /** 422 且目录含投资账户：数据同步异常终态（无重试动作） */
  failLoadingSyncAnomaly: () => void;
  returnToGoal: () => void;
  simulateLoadingError?: boolean;
}
